import React, { useState, useEffect } from 'react';
import { Card, Field, TextField, SaveButton } from '../AdminUI';
import { useSite } from '../../../context/SiteContext';
import { toast } from 'sonner';
import MediaUpload from '../MediaUpload';
import { Switch } from '../../ui/switch';
import { Sparkles, X, Clock, Play } from 'lucide-react';

export default function FlyerAdSection() {
  const { data, update } = useSite();
  const raw = data.flyerAd || {};

  const [flyer, setFlyer] = useState({
    enabled: Boolean(raw.enabled !== false && raw.enabled !== 'false'),
    mediaType: raw.mediaType || 'image',
    imageUrl: raw.imageUrl || 'https://images.unsplash.com/photo-1518609878373-06d740f60d8b?w=800&auto=format&fit=crop&q=80',
    videoUrl: raw.videoUrl || '',
    title: raw.title || 'Welcome to Official Reddy Anna!',
    message: raw.message || 'Get instant B2B Master Admin IDs & B2C User IDs with up to 16% Share Rate. Official 24/7 Verified Support.',
    ctaText: raw.ctaText || 'GET B2B & B2C ID NOW',
    ctaLink: raw.ctaLink || '#exchanges',
    duration: raw.duration || 6,
    autoClose: raw.autoClose !== false
  });

  useEffect(() => {
    if (data.flyerAd) {
      setFlyer({
        enabled: Boolean(data.flyerAd.enabled !== false && data.flyerAd.enabled !== 'false'),
        mediaType: data.flyerAd.mediaType || 'image',
        imageUrl: data.flyerAd.imageUrl || '',
        videoUrl: data.flyerAd.videoUrl || '',
        title: data.flyerAd.title || '',
        message: data.flyerAd.message || '',
        ctaText: data.flyerAd.ctaText || 'GET ID NOW',
        ctaLink: data.flyerAd.ctaLink || '#exchanges',
        duration: data.flyerAd.duration || 6,
        autoClose: data.flyerAd.autoClose !== false
      });
    }
  }, [data.flyerAd]);

  const save = () => {
    const updatedFlyer = {
      ...flyer,
      enabled: Boolean(flyer.enabled)
    };
    update((prev) => ({
      ...prev,
      flyerAd: updatedFlyer
    }));
    // Reset dismissal in session so admin can preview changes
    sessionStorage.removeItem('flyer_ad_dismissed');
    toast.success('Flyer Ad popup settings saved successfully');
  };

  return (
    <div className="space-y-6">
      <Card
        title="Flyer Ad Popup Settings"
        desc="Dynamic flyer popup shown when users open the site. Features cross icon close, dynamic second timer, and automatic close after duration."
        footer={<SaveButton onClick={save} />}
      >
        <div className="space-y-5">
          {/* Enable / Disable Control Button */}
          <div className="flex items-center justify-between p-4 rounded-xl border border-zinc-800 bg-zinc-900/60">
            <div>
              <div className="text-sm font-bold text-white flex items-center gap-2">
                <Sparkles size={16} className="text-[#ccc77d]" />
                <span>Enable Flyer Ad Popup</span>
              </div>
              <p className="text-xs text-zinc-400 mt-0.5">
                When enabled, the flyer ad appears automatically when visitors load the homepage.
              </p>
            </div>
            <Switch
              checked={Boolean(flyer.enabled)}
              onCheckedChange={(val) => setFlyer({ ...flyer, enabled: val })}
              data-testid="flyer-ad-toggle"
            />
          </div>

          {/* Media Type Selection: Image, Video, or Text Only */}
          <div>
            <label className="text-xs font-semibold text-zinc-300 mb-2 block">Media Format</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'image', label: 'Image Flyer', icon: '🖼️' },
                { id: 'video', label: 'Video Flyer', icon: '🎬' },
                { id: 'text', label: 'Text Banner', icon: '📝' }
              ].map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setFlyer({ ...flyer, mediaType: t.id })}
                  className={`p-3 rounded-lg border text-xs font-bold flex flex-col items-center gap-1.5 transition ${
                    flyer.mediaType === t.id
                      ? 'bg-[#ccc77d]/15 border-[#ccc77d] text-white shadow-xs'
                      : 'bg-zinc-900/40 border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800/60'
                  }`}
                >
                  <span className="text-base">{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Image Upload / URL when type is Image */}
          {flyer.mediaType === 'image' && (
            <div className="space-y-3 p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30">
              <label className="text-xs font-semibold text-white block">Flyer Image (Upload or Paste URL)</label>
              <MediaUpload
                value={flyer.imageUrl || ''}
                onChange={(v) => setFlyer({ ...flyer, imageUrl: v })}
                accept="image"
                previewClass="h-28 w-48 object-cover rounded-lg"
                testid="flyer-image-upload"
              />
              <Field label="Or Direct Image URL">
                <TextField
                  value={flyer.imageUrl}
                  onChange={(v) => setFlyer({ ...flyer, imageUrl: v })}
                  placeholder="https://example.com/banner.jpg"
                />
              </Field>
            </div>
          )}

          {/* Video URL when type is Video */}
          {flyer.mediaType === 'video' && (
            <div className="space-y-3 p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30">
              <label className="text-xs font-semibold text-white block">Flyer Video (MP4 / WebM Direct URL or Embed)</label>
              <Field label="Video URL" hint="Direct video link (e.g. .mp4) or YouTube/Vimeo URL. Autoplays muted on desktop & mobile.">
                <TextField
                  value={flyer.videoUrl}
                  onChange={(v) => setFlyer({ ...flyer, videoUrl: v })}
                  placeholder="https://example.com/video.mp4"
                />
              </Field>
            </div>
          )}

          {/* Text Content: Title & Message */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Headline / Ad Title">
              <TextField
                value={flyer.title}
                onChange={(v) => setFlyer({ ...flyer, title: v })}
                placeholder="Exclusive Offer / Welcome Bonus"
              />
            </Field>

            <Field label="Button Text (CTA)">
              <TextField
                value={flyer.ctaText}
                onChange={(v) => setFlyer({ ...flyer, ctaText: v })}
                placeholder="GET ID NOW / CLAIM BONUS"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Button Destination Link" hint="e.g. #exchanges, #get-id, WhatsApp link, Telegram link, or custom URL">
              <TextField
                value={flyer.ctaLink}
                onChange={(v) => setFlyer({ ...flyer, ctaLink: v })}
                placeholder="#exchanges"
              />
            </Field>

            <Field label="Dynamic Duration (Seconds)" hint="How many seconds the ad shows before automatically closing">
              <input
                type="number"
                min={2}
                max={60}
                value={flyer.duration}
                onChange={(e) => setFlyer({ ...flyer, duration: Math.max(2, parseInt(e.target.value, 10) || 5) })}
                className="w-full h-9 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-white text-xs font-bold"
              />
            </Field>
          </div>

          <Field label="Flyer Description / Subtext">
            <textarea
              rows={3}
              value={flyer.message}
              onChange={(e) => setFlyer({ ...flyer, message: e.target.value })}
              placeholder="Enter promotional text or offer details..."
              className="w-full p-3 rounded-md bg-zinc-900 border border-zinc-800 text-white text-xs resize-none"
            />
          </Field>

          {/* Auto-Close Switch */}
          <div className="flex items-center justify-between p-3.5 rounded-lg border border-zinc-800 bg-zinc-900/40">
            <div>
              <div className="text-xs font-bold text-white flex items-center gap-1.5">
                <Clock size={14} className="text-[#ccc77d]" />
                <span>Auto-Close After Duration</span>
              </div>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                Automatically close the ad when the timer reaches 0 seconds.
              </p>
            </div>
            <Switch
              checked={flyer.autoClose}
              onCheckedChange={(val) => setFlyer({ ...flyer, autoClose: val })}
            />
          </div>
        </div>
      </Card>

      {/* Live Preview Card */}
      <Card title="Live Ad Preview" desc="Real-time look of the dynamic flyer modal when a visitor opens the site">
        <div className="relative max-w-sm mx-auto rounded-2xl overflow-hidden bg-zinc-950 border border-zinc-700 shadow-2xl">
          {/* Top Cross Icon */}
          <div className="absolute top-2.5 right-2.5 z-20 flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-full bg-black/70 text-[10px] font-mono font-bold text-white border border-white/20">
              {flyer.duration}s
            </span>
            <span className="w-7 h-7 rounded-full bg-black/80 text-white flex items-center justify-center border border-white/20">
              <X size={14} />
            </span>
          </div>

          {/* Media Header */}
          {flyer.mediaType === 'image' && flyer.imageUrl ? (
            <div className="w-full h-44 bg-zinc-900 overflow-hidden">
              <img src={flyer.imageUrl} alt="preview" className="w-full h-full object-cover" />
            </div>
          ) : flyer.mediaType === 'video' ? (
            <div className="w-full h-44 bg-black flex items-center justify-center text-zinc-500">
              <Play size={32} className="text-[#ccc77d]" />
            </div>
          ) : (
            <div className="w-full h-24 bg-gradient-to-r from-[#777544] to-[#3e3d26] flex items-center justify-center p-4">
              <Sparkles size={24} className="text-white" />
            </div>
          )}

          {/* Body */}
          <div className="p-4 space-y-2 text-center">
            <h4 className="text-white font-black text-base">{flyer.title || 'Promotional Offer'}</h4>
            <p className="text-zinc-300 text-xs leading-relaxed">{flyer.message}</p>
            <div className="pt-2">
              <button
                type="button"
                className="w-full h-9 rounded-full bg-[#ccc77d] text-black font-extrabold text-xs shadow-md"
              >
                {flyer.ctaText || 'CLAIM NOW'}
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
